import { createSelector } from '@reduxjs/toolkit';

import { DirectedAcyclicGraph } from '@paragon/client-tools';
import { JackGirderConnection } from '@paragon/contract-library';

import { RootState } from '..';
import { getActiveComponentDesignGuid } from './getActiveComponentDesignSelector';
import { getNaturalSortedComponentDesigns } from './getSortedComponentDesignsSelectors';

export const getJackGirderConnections = createSelector(
  (state: RootState) => state.jackGirderConnections,
  (jackGirderConnectionsStore) => Object.values(jackGirderConnectionsStore.data),
);

export const getJackGirderConnectionsByGirderGuid = createSelector(getJackGirderConnections, (connections) => {
  const connectionsByGirderGuid: Record<string, JackGirderConnection[]> = {};
  for (const connection of connections) {
    (connectionsByGirderGuid[connection.girderGuid] ??= []).push(connection);
  }
  return connectionsByGirderGuid;
});

const getDirectedAcyclicGraph = createSelector(
  (state: RootState) => state.componentDesigns.data,
  getJackGirderConnections,

  (componentDesigns, jackGirderConnections) =>
    DirectedAcyclicGraph.fromComponentDesignsAndJackGirderConnections(
      Object.values(componentDesigns),
      jackGirderConnections,
    ),
);

export const getCarriedComponentDesignCandidatesForActiveComponentDesign = createSelector(
  getActiveComponentDesignGuid,
  getNaturalSortedComponentDesigns,
  getDirectedAcyclicGraph,

  (activeComponentDesignGuid, componentDesigns, directedAcyclicGraph) => {
    if (!activeComponentDesignGuid) return [];

    const wouldCreateCarriedComponentCycle = (carriedComponentCandidateGuid: string) =>
      directedAcyclicGraph.getDescendantGuids(carriedComponentCandidateGuid).includes(activeComponentDesignGuid);
    return componentDesigns.filter(
      (design) => design.guid !== activeComponentDesignGuid && !wouldCreateCarriedComponentCycle(design.guid),
    );
  },
);
